import config from "@/config";

/**
 * Gromor eSign — clickwrap. Approved 10 Sep 2026.
 *
 * Gromor generates the agreement; we display it on OUR screen; the tenant ticks a box, and that
 * tick IS the signature. No Digio, no signing page, no redirect, no new tab — the tenant never
 * leaves the journey. So unlike the Fexprime/Fintree path there is no `esign/start-digio` and no
 * `session_url` here: three calls, all against our own backend.
 *
 * ── WHY POLL ──────────────────────────────────────────────────────────────────────────────
 * "Next" asks Gromor to generate the agreement; it arrives on a callback to our backend, not in
 * the response. Gromor's callbacks have been measured at anything from 80 seconds to
 * "never" — so the screen polls, changes its copy as the wait lengthens, and after a few minutes
 * offers a way back to the journey. Nothing is lost by leaving: every leg is recorded on the
 * backend, and the hub's E-Sign card re-enters this flow where it left off.
 */

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem(config.LOCAL_KEYS.SIGNUP_TOKEN)}`,
});

async function call(path, init = {}) {
  const response = await fetch(`${config.API_URL}/gromor-esign/${path}`, {
    headers: authHeaders(),
    ...init,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body?.message || "Something went wrong. Please try again.");
  }
  return body?.data ?? null;
}

/**
 * The tenant tapped Next. Sends the proposal (deposit included only if `maintenanceOn`), gets
 * the KFS, asks Gromor to generate the agreement. Idempotent server-side — a double-tap or a
 * remount cannot send the proposal twice.
 */
export function startGromorEsign({ maintenanceOn }) {
  return call("start", { method: "POST", body: JSON.stringify({ maintenanceOn }) });
}

/** One read the whole flow renders from. Safe to repeat. */
export function fetchGromorEsignStatus() {
  return call("status", { method: "GET" });
}

/**
 * The tick. IP and user agent are read by the backend from the request itself; sending nothing
 * here is deliberate — a value typed into a body could be anything.
 */
export function recordGromorEsignConsent() {
  return call("consent", { method: "POST" });
}

/**
 * Poll until `done(status)` is true, or the budget runs out. Resolves with the last status
 * either way — the caller decides what "not done yet" means on screen.
 */
export async function pollGromorEsignUntil(done, { intervalMs = 3000, maxMs = 180000 } = {}) {
  const startedAt = Date.now();
  let last = null;
  while (Date.now() - startedAt < maxMs) {
    try {
      last = await fetchGromorEsignStatus();
      if (last && done(last)) return last;
    } catch (error) {
      // A failed poll is not a failed eSign — keep going.
      console.error("Gromor eSign poll failed", error);
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return last;
}

/**
 * Our stored copy of the unsigned agreement, served the same way every other tenant document
 * is (`GET /file/:userId?path=…`). The backend wrote it at `{userId}/esign/{refId}-agreement.pdf`
 * the moment Gromor's callback landed, because Gromor's own URL dies in ~15 minutes.
 */
export function gromorAgreementUrl(userId, refId) {
  const token = localStorage.getItem(config.LOCAL_KEYS.SIGNUP_TOKEN);
  return `${config.API_URL}/file/${userId}?path=esign/${refId}-agreement.pdf&token=${token}`;
}
